import React, { ReactNode } from "react";
import { PathInfo } from "./PathInfo";
import { Box, Checkbox } from "@mui/material";
import { RemovePropertyButton } from "./RemovePropertyButton";
import { TextComponent } from "./TextComponent";
import { ImagePreview } from "./ImagePreview";
import { conditionedAttribute, getUnconditionalName } from "./Conditional/HelpersConditional";
import ConditionalMiddleware from "./Conditional/ConditionalMiddleware";
import { OpenClickableAreasEditorButton } from "./OpenClickableAreasEditorButton";
import { OpenPositionsEditorButton } from "./OpenPositionsEditorButton";
import { OpenClipPathEditorButton } from "./OpenClipPathEditorButton";
import { OpenFilterEditorButton } from "./OpenFilterEditorButton";
import { NumberComponent } from "./NumberComponent";
import { BooleanComponent } from "./BooleanComponent";

interface GamePiecePropertyProps {
  propName: string;
  value: any;
  fullObject: any;
  path: string[];
}

export function GamePieceProperty(props: GamePiecePropertyProps) {
  const propertyName = getUnconditionalName(props.propName);
  const conditionedValue = conditionedAttribute(props.fullObject, propertyName);

  const renderPropertyValue = () => {
    let innerContentFunction = (value: any): ReactNode => <></>;
    switch (propertyName) {
      case "image":
      case "cardBack":
        innerContentFunction = (value: any) => <ImagePreview propName={propertyName} value={value} />;
        break;
      case "clickableAreas":
        innerContentFunction = (value: any) => (
          <OpenClickableAreasEditorButton propName={propertyName} value={value} fullObject={props.fullObject} />
        );
        break;
      case "positions":
        innerContentFunction = (value: any) => <OpenPositionsEditorButton propName={propertyName} value={value} />;
        break;
      case "clipPath":
        innerContentFunction = (value: any) => <OpenClipPathEditorButton propName={propertyName} value={value} />;
        break;
      case "filter":
        innerContentFunction = (value: any) => <OpenFilterEditorButton propName={propertyName} value={value} />;
        break;
      case "selectable":
      case "displayLastOnly":
      case "editModeSelectable":
        innerContentFunction = (value: any) => (
          <BooleanComponent key={value.toString()} propName={propertyName} value={value} />
        );
        break;
      case "x":
      case "y":
      case "width":
      case "height":
        innerContentFunction = (value: any) => <NumberComponent key={value} propName={propertyName} value={value} />;
        break;
      default:
        innerContentFunction = (value: any) => (
          <TextComponent key={value.toString()} propName={propertyName} value={value} />
        );
    }

    function innerComponentWithPathInfo(value: any, conditionalPropName: string) {
      return (
        <PathInfo name={conditionalPropName}>
          <Box className={"game-piece-property-controls"}>
            <RemovePropertyButton propName={propertyName} />
          </Box>
          <Box className={"game-piece-property-content"}>{innerContentFunction(value)}</Box>
        </PathInfo>
      );
    }

    return (
      <ConditionalMiddleware
        propertyName={propertyName}
        conditions={conditionedValue}
        originalComponent={innerComponentWithPathInfo}
      />
    );
  };

  return <Box className={"game-piece-property"}>{renderPropertyValue()}</Box>;
}
